import { useCallback, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AppShell } from "./layout/AppShell";
import { SYSTEM_SENT_URL } from "./api/mailbox";
import type { SectionNavId, SectionNavItem } from "./layout/SectionNav";
import { AuthProvider } from "./features/auth/AuthProvider";
import { LoginPage } from "./features/auth/LoginPage";
import { ClientPortal } from "./features/client/ClientPortal";
import { ForgotPasswordPage } from "./features/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./features/auth/ResetPasswordPage";
import { useAuth } from "./features/auth/useAuth";
import { QuoteList } from "./features/quotes/QuoteList";
import { ArchivePage } from "./features/quotes/ArchivePage";
import { QuoteEditorPage } from "./features/quotes/QuoteEditorPage";
import { NewQuotePage } from "./features/quotes/NewQuotePage";
import { TemplateList } from "./features/templates/TemplateList";
import { CatalogPage } from "./features/catalog/CatalogPage";
import { CompanyList } from "./features/companies/CompanyList";
import { WorkOrdersPage } from "./features/workorders/WorkOrdersPage";
import { WorkOrderEditorPage } from "./features/workorders/WorkOrderEditorPage";
import { InvoicesPage } from "./features/invoices/InvoicesPage";
import { ExpensesPage } from "./features/expenses/ExpensesPage";
import { ReportsPage } from "./features/reports/ReportsPage";
import { MeetingsPage } from "./features/meetings/MeetingsPage";
import { MeetingNotePage } from "./features/meetings/MeetingNotePage";
import { HomePage } from "./features/home/HomePage";
import { MailboxPage } from "./features/mailbox/MailboxPage";
import { LeavePage } from "./features/leave/LeavePage";
import { AttendancePage } from "./features/attendance/AttendancePage";
import { TrashPage } from "./features/trash/TrashPage";
import { SettingsPage } from "./features/settings/SettingsPage";
import { BulletinPage } from "./features/bulletin/BulletinPage";
import { UpdatesPage } from "./features/updates/UpdatesPage";
import { KioskPage } from "./features/kiosk/KioskPage";
import type { CommandLineTarget } from "./features/command/CommandPalette";
import type { CatalogHit, TemplateSummary } from "./api/types";

// 路由。側邊欄的每一個連結都要真的接到畫面 —— 元件測試直接 render 元件、
// 繞過路由,所以「元件好好的、但沒有任何網址接得到它」在元件測試裡看不出來,
// 由 tests/app-routes.test.tsx 從網址進去守著。
//
// /login、/forgot、/reset 之外的每一頁都要先登入。守衛擋下來時會記住原本要去的
// 網址,登入成功後再送回去 —— 否則使用者點進來的連結會在登入後憑空消失。
//
// 忘記密碼與重設那兩頁必須留在守衛外面:會走到那裡的人正是進不去的那一個,
// 被守衛擋住就等於整個功能不存在。

/** 頂列標題:一眼看出自己在哪一頁。 */
const TITLES: Array<[RegExp, string]> = [
  [/^\/quotes\/new$/, "新增報價單"],
  // 封存區排在 /^\/quotes/ 之前,否則它會先被那一條吃掉,頂列寫著「報價單」——
  // 而使用者正站在一個列表上一張都看不到的地方,那個標題只會讓人以為系統壞了。
  [/^\/quotes\/archive$/, "封存區"],
  [/^\/quotes\/\d+$/, "報價單"],
  [/^\/quotes/, "報價單"],
  [/^\/templates/, "範本"],
  [/^\/catalog/, "品項庫"],
  [/^\/companies/, "客戶"],
  [/^\/workorders\/new$/, "新增工單"],
  [/^\/workorders/, "製單"],
  [/^\/invoices/, "發票"],
  [/^\/expenses/, "雜支"],
  [/^\/reconcile/, "對帳"],
  [/^\/reports/, "財務報表"],
  [/^\/meetings/, "會議紀錄"],
  [/^\/home/, "首頁"],
  [/^\/mailbox/, "信箱"],
  [/^\/leave/, "請假"],
  [/^\/settings/, "設定"],
];

function titleFor(pathname: string): string {
  const hit = TITLES.find(([pattern]) => pattern.test(pathname));
  return hit ? hit[1] : "報價單";
}

function NotFound() {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-3 py-12">
      <h2 className="m-0 text-xl font-semibold text-[var(--text)]">找不到這個頁面</h2>
      <p className="m-0 text-[var(--text-2)]">
        網址可能打錯了,或這一頁已經移走。
        <Link to="/quotes" className="ml-1 text-[var(--accent)] underline">
          回報價單清單
        </Link>
      </p>
    </div>
  );
}

/** 還在問後端「我是誰」的空檔。這時候還不知道有沒有登入,不可以先把人踢去登入頁。 */
function AuthLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg)]">
      <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>
    </div>
  );
}

/** 路由守衛。未登入一律導向 /login,並把原本要去的網址帶在 state 裡。 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <AuthLoading />;
  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return <>{children}</>;
}

/** /login。已經登入的人不該再看到登入表單。 */
function LoginRoute() {
  const { status } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  if (status === "loading") return <AuthLoading />;
  if (status === "authenticated") {
    // 沒有來處(直接開 /login),就送去首頁;來處是 /login 本身則忽略,免得原地打轉。
    // **登入後的落點是首頁**(業主 2026-09-17:「每個人進到系統後 默認進入首頁」)。
    // 有來處就回來處 —— 他本來要去的那一頁比首頁更重要。
    return <Navigate to={from && !from.startsWith("/login") ? from : "/home"} replace />;
  }
  return <LoginPage />;
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        {/* 公開路由。不包 RequireAuth —— 忘記密碼的人本來就沒有 session。 */}
        <Route path="/forgot" element={<ForgotPasswordPage />} />
        <Route path="/reset" element={<ResetPasswordPage />} />
        {/* 客戶那一側(第 14 輪)。**不包 RequireAuth** ——
            包了的話客戶會被導去員工的登入頁,而他沒有員工帳號,
            那一頁對他來說是一面永遠過不去的牆。
            後端也是分開的:另一張表、另一個 cookie 名稱、另一個簽章 salt。 */}
        <Route path="/client" element={<ClientPortal />} />
        {/* 門口打卡機(業主 2026-09-25)。**不包 RequireAuth** —— 它是一台沒有人
            登入著的公用機器;守門的是它自己的 kiosk cookie(後端 routers/kiosk.py)。
            也不套外框:直式觸控螢幕,全螢幕、沒有側邊欄。 */}
        <Route path="/kiosk" element={<KioskPage />} />
        <Route
          path="*"
          element={
            <RequireAuth>
              <AuthedApp />
            </RequireAuth>
          }
        />
      </Routes>
    </AuthProvider>
  );
}

/** 登入後的整個站台:外框 + 各頁路由。 */
function AuthedApp() {
  const navigate = useNavigate();
  const location = useLocation();
  // 側邊欄下半段的報價分類導覽。由開啟中的編輯器回報,離開時清空。
  const [sections, setSections] = useState<SectionNavItem[]>([]);
  // ⌘K 的「本單明細」。同樣由開啟中的編輯器回報,離開時清空 ——
  // 沒有這一條,指令面板的搜尋框寫著「本單明細」卻永遠列不出那一組結果。
  const [lines, setLines] = useState<CommandLineTarget[]>([]);

  // 開著編輯器時,「套用範本」與「品項庫帶入」由編輯器自己處理;
  // 這裡只留一個掛鉤,讓指令面板找得到它。沒有編輯器時退回導頁 ——
  // 少了這兩條線,⌘K 搜到範本只會把人丟去範本頁,等於搜到了卻套不了。
  const applyTemplateRef = useRef<((template: TemplateSummary) => void) | null>(null);
  const pickCatalogRef = useRef<((hit: CatalogHit) => void) | null>(null);

  const registerApplyTemplate = useCallback((handler: ((template: TemplateSummary) => void) | null) => {
    applyTemplateRef.current = handler;
  }, []);

  const registerPickCatalog = useCallback((handler: ((hit: CatalogHit) => void) | null) => {
    pickCatalogRef.current = handler;
  }, []);

  const handleApplyTemplate = useCallback(
    (template: TemplateSummary) => {
      if (applyTemplateRef.current) {
        applyTemplateRef.current(template);
        return;
      }
      // 當下不在任何一張報價單上:先開新單,建立後再把範本接過去。
      navigate(`/quotes/new?template=${template.id}`);
    },
    [navigate],
  );

  const handlePickCatalog = useCallback(
    (hit: CatalogHit) => {
      if (pickCatalogRef.current) {
        pickCatalogRef.current(hit);
        return;
      }
      navigate("/catalog");
    },
    [navigate],
  );

  // 章節卡片的 DOM id 是 nq-section-{uid},導覽項目用的也是同一個 uid。
  const jumpSection = useCallback((id: SectionNavId) => {
    const target = document.getElementById(`nq-section-${id}`);
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // 明細列的 DOM id 是 nq-line-{uid}。捲過去還不夠 —— 游標要真的落在那一格,
  // 使用者按 Enter 之後就能直接開始打字,不必再自己找一次。
  const jumpLine = useCallback((id: number | string) => {
    const row = document.getElementById(`nq-line-${id}`);
    if (!row) return;
    if (typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    row.querySelector<HTMLElement>("textarea,input,select")?.focus();
  }, []);

  // 業主原話:「只有點進去報價單 左側才出現本單章節」。
  // 編輯器卸載時已經會把 sections 清空,這裡再用網址擋一次 ——
  // 少了這一道,任何一條忘記清空的路徑都會讓上一單的分類跟著人跑到別頁。
  const onQuoteEditor = /^\/quotes\/\d+$/.test(location.pathname);

  return (
    <AppShell
      topBar={{ title: titleFor(location.pathname) }}
      sections={onQuoteEditor ? sections : []}
      onJumpSection={jumpSection}
      onApplyTemplate={handleApplyTemplate}
      onPickCatalog={handlePickCatalog}
      lines={lines}
      onJumpLine={jumpLine}
    >
      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route
          path="/quotes"
          element={
            <QuoteList
              onOpen={(id) => navigate(`/quotes/${id}`)}
              onCreate={() => navigate("/quotes/new")}
              // 三條動線都要接:少接一條,那一顆就落在 QuoteList 的退路
              // (window.location.assign)上 —— 整頁重新載入,bundle 重下載、
              // /auth/me 與未讀數重打、前端狀態歸零,螢幕閱讀器的頁面語境也
              // 重來一次。而封存區的反向動線(「回報價單列表」)一直是正規的
              // <Link>,兩邊不一致只會讓人以為封存區特別慢。
              onOpenArchive={() => navigate("/quotes/archive")}
              onArchived={(url) => navigate(url)}
            />
          }
        />
        <Route path="/quotes/new" element={<NewQuotePage />} />
        {/* 封存區。業主寫的是「報價單 > 封存」,所以是報價單底下的一層,
            不在側邊欄另開一項(側邊欄已經七項了)。這一條要排在 /quotes/:id
            前面:雖然 React Router 會讓靜態片段贏過動態片段,但讀的人不必為了
            確認這件事去翻文件。 */}
        <Route path="/quotes/archive" element={<ArchivePage />} />
        <Route
          path="/quotes/:id"
          element={
            <QuoteEditorPage
              onSections={setSections}
              onLines={setLines}
              onRegisterApplyTemplate={registerApplyTemplate}
              onRegisterPickCatalog={registerPickCatalog}
            />
          }
        />
        <Route path="/templates" element={<TemplateList />} />
        <Route path="/catalog" element={<CatalogPage />} />
        <Route path="/companies" element={<CompanyList />} />
        {/* 製單(業主 2026-10-08)。廠商分頁 2026-10-15 拿掉了,舊網址轉回列表。 */}
        <Route path="/workorders" element={<WorkOrdersPage />} />
        <Route path="/workorders/vendors" element={<Navigate to="/workorders" replace />} />
        <Route path="/workorders/new" element={<WorkOrderEditorPage />} />
        <Route path="/workorders/:id" element={<WorkOrderEditorPage />} />
        {/* 寄件紀錄併進信箱的「系統寄出」(2026-10-05);舊連結與書籤轉過去 */}
        <Route path="/outbox" element={<Navigate to={SYSTEM_SENT_URL} replace />} />
        {/* 發票是財務的頁面(第 15 輪)。角色過濾在 navRoles.ts;
            這裡不另外擋 —— 後端的 require_finance 才是說了算的那一層。 */}
        <Route path="/invoices" element={<InvoicesPage />} />
        {/* 全公司的雜支清單是財務的頁面(第 16 輪)。登錄的入口在報價單上。 */}
        <Route path="/expenses" element={<ExpensesPage />} />
        {/* 月 / 季 / 年報與業務績效(第 17 輪)。角色過濾在 navRoles.ts;
            說了算的那一層是後端的 require_finance。 */}
        {/* 銷項與進項的兩張總表(第 22 輪)。2026-09-30 併進報表頁的「對帳」分頁,
            舊網址(書籤、通知裡的連結)轉過去。 */}
        <Route path="/reconcile" element={<Navigate to="/reports?tab=reconcile" replace />} />
        <Route path="/reports" element={<ReportsPage />} />
        {/* 會議紀錄 / 結案報告 / 檢討報告(第 18 輪)。全員看得到,
            改與刪限作者本人與老闆 —— 說了算的那一層在後端。 */}
        <Route path="/meetings" element={<MeetingsPage />} />
        {/* 寫 / 改一份是**一整頁**,不是懸浮視窗(業主 2026-09-17)。
            `/meetings/new` 新增,`/meetings/12` 是第 12 篇 ——
            助理回的連結指的就是後者。 */}
        <Route path="/meetings/:id" element={<MeetingNotePage />} />
        {/* 首頁。**每個人登入後的落點**(業主 2026-09-17)。 */}
        <Route path="/home" element={<HomePage />} />
        {/* 信箱要在系統裡開,不另開一個瀏覽器視窗(業主原話)。
            個人信箱第二階段起讀的是本人信箱的系統備份。 */}
        <Route path="/mailbox" element={<MailboxPage />} />
        {/* 請假。業主要的入口在側邊欄「設定」上方,路由本身沒有分權 ——
            所有人看得到所有人的假,只有「裁決」那一段是老闆專屬。 */}
        <Route path="/leave" element={<LeavePage />} />
        {/* 公佈欄(業主 2026-09-25)。全員;門口的打卡機輪播它。 */}
        <Route path="/bulletin" element={<BulletinPage />} />
        <Route path="/updates" element={<UpdatesPage />} />
        {/* /settings、/settings/columns、/settings/profile 與 /settings/users 是同一頁的分頁,
            分頁狀態由網址決定 —— 老闆才能把「列表欄位」「使用者管理」存成書籤,
            重新整理也還在同一個分頁。
            三條各自列出來而不用 /settings/*:splat 路由會讓 /settings/亂打
            也落在設定頁上,那一頁該是「找不到這個頁面」。 */}
        <Route path="/attendance" element={<AttendancePage />} />
        <Route path="/trash" element={<TrashPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/columns" element={<SettingsPage />} />
        <Route path="/settings/profile" element={<SettingsPage />} />
        <Route path="/settings/users" element={<SettingsPage />} />
        <Route path="/settings/kiosks" element={<SettingsPage />} />
        <Route path="/settings/reviewers" element={<SettingsPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppShell>
  );
}
